/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * How many seats of each plan the agency buys.
 *
 * Adding is money today, pro rata to the days left. Reducing is not refunded
 * and lands at the renewal, which is why a reduction asks who leaves before
 * it can be scheduled — on the date, somebody has to be the one whose access
 * ends, and the screen does not get to pick.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button, Text } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import { refusalOf } from '../../hooks/previewRefusal';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import type {
  ChangeItemsPreview, TeamOverview,
} from '@/services/requests/housien-services/AgenciesService.team';
import {
  asItems, deltas, draftFrom, isDirty, meetsFloor,
  step, totalSeats, type SeatDraft, type SeatPrice,
} from './seat-draft';
import {
  alignToContracted, candidatesFor, isComplete, leavingPayload, preChosen,
  seatsGivenUp,
  type LeavingEntry,
} from './seat-reduction';
import { LeavingPicker } from './LeavingPicker';
import { PreviewLine, ScheduledBand, SeatDelta, SeatStepper } from './SeatsBlock.parts';

/** The subscription floor, the same one the checkout enforces. */
const SUBSCRIPTION_SEAT_FLOOR = 2;

/** The confirm button label for a seat change: leaving swap, reduction, pending quote, or pay. */
function payLabel(
  swapping: boolean,
  growing: boolean,
  quoted: boolean,
  price: string,
  t: (key: string, options?: { price: string }) => string,
): string {
  if (swapping) return t('v2.seats.saveLeaving');
  if (!growing) return t('v2.seats.scheduleReduction');
  if (!quoted) return t('v2.seats.confirmPending');
  return t('v2.seats.confirmAndPay', { price });
}

/** The toast key for a saved seat change: leaving swap, reduction, or change. */
function savedKey(swapping: boolean, reducing: boolean): string {
  if (swapping) return 'v2.seats.leavingSaved';
  return reducing ? 'v2.seats.reductionScheduled' : 'v2.seats.seatsChanged';
}

/** The toast key for a refused seat change; a pending payment is named. */
function failedKey(error: unknown): string {
  return refusalOf(error) === 'payment_pending'
    ? 'v2.seats.addModal.trial.paymentPending'
    : 'v2.seats.changeFailed';
}

export interface SeatsBlockProps {
  agencyId: string;
  team: TeamOverview | null;
  /** What the subscription holds today, one entry per plan. */
  items: Array<{ plan_id: string; quantity: number }>;
  prices: SeatPrice[];
  planTitle: (planId: string) => string | null;
  maxSeats: (planId: string) => number | null;
  /** Every identifier a plan answers to, so a schedule can be matched to it. */
  planAliases: (planId: string) => string[];
  /**
   * Auxiliary users one seat on this plan brings. The draft changes them
   * too, and a reduction that quietly costs the agency ten free places is
   * worth seeing before it is confirmed rather than after.
   */
  supportPerSeat?: (planId: string) => number;
  nextRenewalDate: string | null;
  billingCycleDays: number;
  /** Reloads the subscription and team after a change lands. */
  onChanged: () => void;
}

/**
 * The seats block.
 *
 * @param props - The subscription's lines, the team in them, and the prices.
 * @returns The block.
 */
export const SeatsBlock: React.FC<SeatsBlockProps> = ({
  agencyId, team, items, prices, planTitle, maxSeats, planAliases, supportPerSeat,
  nextRenewalDate, billingCycleDays, onChanged,
}) => {
  const { t, i18n } = useTranslation('features/subscription');
  const [draft, setDraft] = useState<SeatDraft>(() => draftFrom(items));
  const [leaving, setLeaving] = useState<LeavingEntry[]>([]);
  const [busy, setBusy] = useState(false);
  // Changing who leaves a reduction that already exists. The seats are not
  // moving -- the schedule is the same one -- so there is nothing to charge
  // and nothing to send to the broker; only the names change.
  const [swapping, setSwapping] = useState(false);

  // The contracted lines are the truth; the draft is what the steppers are
  // asking for. A change landing elsewhere resets the asking.
  useEffect(() => { setDraft(draftFrom(items)); }, [JSON.stringify(items)]);

  // Spoken back in the identifiers the contract uses: Payien answers in the
  // catalogue's own plan id while the host asks in slugs, and nothing matched.
  const scheduled = useMemo(
    () => alignToContracted(team?.scheduled_items_change, items, planAliases),
    [team?.scheduled_items_change, items, planAliases],
  );
  const blocks = team?.seats.by_plan ?? [];
  const ownerPlanId = useMemo(() => (
    (team?.people ?? []).find((person) => person.role === 'owner')?.plan_id ?? null
  ), [team]);

  const moved = deltas(items, draft);
  const dirty = isDirty(items, draft);
  const givenUp = useMemo(() => {
    const going: Record<string, number> = {};
    Object.entries(moved).forEach(([planId, by]) => { if (by < 0) going[planId] = -by; });
    return going;
  }, [JSON.stringify(moved)]);

  // A block being reduced opens with its free seats marked: the only choice
  // that costs nobody anything is the one the screen should have made.
  useEffect(() => {
    setLeaving((current) => {
      const stale = current.filter((entry) => givenUp[entry.plan_id]);
      const missing = Object.entries(givenUp).filter(
        ([planId]) => !stale.some((entry) => entry.plan_id === planId),
      );
      const seeded = missing.flatMap(([planId, capacity]) => preChosen(
        planId, blocks.find((block) => block.plan_id === planId), capacity,
      ));
      return [...stale, ...seeded];
    });
  }, [JSON.stringify(givenUp), blocks.length]);

  // Asked, not computed. These figures were worked out here from a billing
  // cycle the API does not send, so an annual subscription was prorated over
  // thirty days -- and the label beside the price said "/mês" for a yearly
  // one, for the same reason. Payien prices what Payien collects.
  const [preview, setPreview] = useState<ChangeItemsPreview | null>(null);
  const [quoteFailed, setQuoteFailed] = useState(false);
  // The first payment is still being confirmed: say so, offer no retry.
  const [paymentPending, setPaymentPending] = useState(false);
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  useEffect(() => {
    if (!isDirty(items, draft)) {
      setPreview(null);
      setQuoteFailed(false);
      setPaymentPending(false);
      return undefined;
    }
    let live = true;
    setPreview(null);
    setQuoteFailed(false);
    setPaymentPending(false);
    AgenciesService.previewSubscriptionItems(agencyId, asItems(draft))
      .then((answer) => { if (live) setPreview(answer); })
      .catch((error) => {
        if (!live) return;
        const refused = refusalOf(error) === 'payment_pending';
        setPreview(null);
        setPaymentPending(refused);
        setQuoteFailed(!refused);
      });
    return () => { live = false; };
  }, [agencyId, JSON.stringify(draft), JSON.stringify(items), quoteAttempt]);

  // No answer is not zero. Defaulting the figures to zero showed "R$ 0,00"
  // for an added seat and turned the button into "schedule a reduction",
  // offering to confirm something that looked free -- the same mistake as
  // the thirty-day fallback this replaced, one layer up.
  const quoted = preview !== null;
  // Only the free period carries a first-charge figure; nothing is collected today.
  const trialing = preview?.next_charge_amount_cents !== undefined;
  const days = preview?.days_remaining ?? 0;
  const chargedToday = preview?.charged_today_cents ?? 0;
  const renewalTotal = preview?.next_charge_amount_cents ?? preview?.new_total_cents ?? 0;
  const cycleDays = preview?.billing_cycle_days ?? billingCycleDays;
  const growing = Object.values(moved).some((by) => by > 0);
  const money = (cents: number) => formatCents(cents, { locale: i18n.language });
  const day = (iso: string | null) => (iso ? formatLocalizedDate(iso, i18n.language) : '');

  const takenOf = (planId: string) => {
    const block = blocks.find((candidate) => candidate.plan_id === planId);
    return block ? block.bought - block.free : 0;
  };

  const complete = isComplete(leaving, givenUp);
  const aboveFloor = meetsFloor(draft, SUBSCRIPTION_SEAT_FLOOR);
  const canConfirm = dirty && aboveFloor && complete && !busy && (quoted || swapping);
  const reducing = Object.keys(givenUp).length > 0;

  const discard = () => {
    setDraft(draftFrom(items));
    setLeaving([]);
    setSwapping(false);
  };

  const confirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      // Not while swapping: the item list is the one already scheduled, so
      // the call would ask the broker to change a subscription into itself.
      if (!swapping) {
        await AgenciesService.changeSubscriptionItems(agencyId, asItems(draft));
      }
      // The choice goes after the change, never before: it is recorded
      // against a reduction that now exists, and sending it first would
      // name seats for a schedule nothing had created.
      if (reducing) {
        await AgenciesService.setSubscriptionLeaving(agencyId, leavingPayload(leaving) as never);
      }
      toast.success(t(savedKey(swapping, reducing)));
      setLeaving([]);
      setSwapping(false);
      onChanged();
    } catch (error) {
      toast.error(t(failedKey(error)));
    } finally {
      setBusy(false);
    }
  };

  const cancelReduction = async () => {
    setBusy(true);
    try {
      // Asking for the list it already has is how a schedule is undone.
      await AgenciesService.changeSubscriptionItems(agencyId, asItems(draftFrom(items)));
      toast.success(t('v2.seats.reductionCancelled'));
      onChanged();
    } catch {
      toast.error(t('v2.seats.changeFailed'));
    } finally {
      setBusy(false);
    }
  };

  const scheduledFor = (planId: string): number | null => {
    const line = (scheduled?.items ?? []).find((entry) => entry.plan_id === planId);
    return line ? line.quantity : null;
  };

  return (
    <div
      data-testid="plan-seats"
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
        padding: 'var(--space-5)', borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
        <Text
          component="h3"
          style={{
            margin: 0, fontFamily: 'var(--font-family-alt-2)',
            fontSize: 'var(--text-app-section)', fontWeight: 'var(--font-bold)',
            color: 'var(--text-primary)',
          }}
        >
          {t('v2.seats.title')}
        </Text>
        <Text
          style={{
            fontSize: 'var(--text-small)', lineHeight: 1.5,
            color: 'var(--text-tertiary-soft)',
          }}
        >
          {t('v2.seats.caption')}
        </Text>
      </div>

      {scheduled && (
        <ScheduledBand
          lines={(scheduled.items ?? []).map((line) => ({
            planId: line.plan_id,
            title: planTitle(line.plan_id) ?? line.plan_id,
            from: items.find((item) => item.plan_id === line.plan_id)?.quantity ?? 0,
            to: line.quantity,
          })).filter((line) => line.to < line.from)}
          effectiveAt={day(scheduled.effective_at ?? null)}
          leaving={(team?.people ?? [])
            .filter((person) => person.leaving_at)
            .map((person) => person.email)}
          onEdit={() => {
            setDraft(draftFrom(scheduled.items));
            setSwapping(true);
          }}
          onCancel={cancelReduction}
        />
      )}

      {items.map((line) => {
        const title = planTitle(line.plan_id) ?? line.plan_id;
        const value = draft[line.plan_id] ?? 0;
        const willBe = scheduledFor(line.plan_id);
        const ceiling = maxSeats(line.plan_id);
        return (
          <div
            key={line.plan_id}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 'var(--space-4)', flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div
                style={{
                  display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)',
                  flexWrap: 'wrap',
                }}
              >
                <Text
                  component="span"
                  style={{
                    fontSize: 'var(--text-sm)', fontWeight: 'var(--font-bold)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {title}
                </Text>
                <SeatDelta delta={moved[line.plan_id] ?? 0} />
                {willBe != null && willBe < line.quantity && (
                  <Text
                    component="span"
                    style={{
                      fontSize: 'var(--text-tag)', fontWeight: 'var(--font-semibold)',
                      color: 'var(--accent-mostarda)',
                    }}
                  >
                    {t('v2.seats.staysWith', {
                      count: willBe, date: day(scheduled?.effective_at ?? null),
                    })}
                  </Text>
                )}
              </div>
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)',
                }}
              >
                {t(cycleDays >= 365 ? 'v2.seats.perSeatYear' : 'v2.seats.perSeatMonth', {
                  price: money(prices.find((p) => p.planId === line.plan_id)?.centsPerSeat ?? 0),
                })}
                {' · '}
                {t('v2.seats.occupied', { count: takenOf(line.plan_id) })}
              </Text>
            </div>
            <SeatStepper
              planId={line.plan_id}
              value={value}
              canRemove={!busy && value > (line.plan_id === ownerPlanId ? 1 : 0)}
              canAdd={!busy && (ceiling == null || value < ceiling)}
              onStep={(by) => setDraft(
                (current) => step(current, line.plan_id, by, ownerPlanId, ceiling),
              )}
            />
          </div>
        );
      })}

      {Object.entries(givenUp).map(([planId, capacity]) => (
        <LeavingPicker
          key={planId}
          planId={planId}
          planTitle={planTitle(planId) ?? planId}
          capacity={capacity}
          candidates={candidatesFor(
            planId,
            blocks.find((block) => block.plan_id === planId),
            (team?.people ?? []).map((person) => ({
              seat_id: person.seat_id, email: person.email,
              plan_id: person.plan_id, role: person.role,
            })),
            (team?.pending ?? []).map((invite) => ({
              id: invite.id, email: invite.email, plan_id: invite.plan_id,
            })),
          )}
          leaving={leaving}
          onChange={setLeaving}
        />
      ))}

      {dirty && (
        <div
          style={{
            display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
            padding: 'var(--space-3-5)', borderRadius: 'var(--radius-lg)',
            background: 'var(--surface-primary)',
          }}
        >
          {paymentPending ? (
            <Text role="alert" data-testid="plan-seats-payment-pending" style={{ fontSize: 'var(--text-small)', lineHeight: 1.5 }}>
              {t('v2.seats.addModal.trial.paymentPending')}
            </Text>
          ) : quoteFailed ? (
            <Text
              style={{
                fontSize: 'var(--text-small)', lineHeight: 1.5,
                color: 'var(--status-error-text)',
              }}
            >
              {t('v2.seats.quoteFailed')}
            </Text>
          ) : (
            <>
              {trialing && growing && (
                <>
                  <div data-testid="plan-seats-trial-today">
                    <PreviewLine
                      label={t('v2.seats.addModal.trial.chargedToday')}
                      value={money(0)}
                      strong
                    />
                  </div>
                  <Text
                    data-testid="plan-seats-trial-enters"
                    style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}
                  >
                    {t('v2.seats.trial.entersFirstCharge', { date: day(nextRenewalDate) })}
                  </Text>
                </>
              )}
              {!trialing && Object.entries(moved).filter(([, by]) => by > 0).map(([planId, by]) => (
                <PreviewLine
                  key={`add-${planId}`}
                  label={quoted
                    ? t('v2.seats.chargedToday', {
                      count: by, plan: planTitle(planId) ?? planId, days,
                    })
                    : t('v2.seats.chargedTodayPending', {
                      count: by, plan: planTitle(planId) ?? planId,
                    })}
                  value={quoted ? money(chargedToday) : t('v2.seats.quoting')}
                  strong
                />
              ))}
              {Object.entries(givenUp).map(([planId, by]) => (
                <PreviewLine
                  key={`cut-${planId}`}
                  label={t('v2.seats.reductionFrom', {
                    count: by, plan: planTitle(planId) ?? planId,
                    date: day(nextRenewalDate),
                  })}
                  value={t('v2.seats.noCharge')}
                />
              ))}
              <PreviewLine
                label={t('v2.seats.nextRenewal', {
                  date: day(nextRenewalDate), count: totalSeats(draft),
                })}
                value={quoted ? money(renewalTotal) : t('v2.seats.quoting')}
              />
              {supportPerSeat ? (() => {
                const after = Object.entries(draft).reduce(
                  (sum, [planId, seats]) => sum + seats * supportPerSeat(planId), 0,
                );
                return after > 0 ? (
                  <PreviewLine
                    label={t('v2.seats.supportAfter')}
                    value={String(after)}
                  />
                ) : null;
              })() : null}
            </>
          )}
          {quoteFailed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setQuoteAttempt((attempt) => attempt + 1)}
              style={{ alignSelf: 'flex-start' }}
            >
              {t('v2.seats.retryQuote')}
            </Button>
          )}
        </div>
      )}

      {dirty && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
            gap: 'var(--space-3)', flexWrap: 'wrap',
          }}
        >
          {!aboveFloor && (
            <Text
              style={{
                fontSize: 'var(--text-small)', color: 'var(--accent-cobre)',
                marginRight: 'auto',
              }}
            >
              {t('v2.seats.belowFloor', { count: SUBSCRIPTION_SEAT_FLOOR })}
            </Text>
          )}
          {aboveFloor && !complete && (
            <Text
              style={{
                fontSize: 'var(--text-small)', color: 'var(--text-tertiary-soft)',
                marginRight: 'auto',
              }}
            >
              {t('v2.seats.pickFirst')}
            </Text>
          )}
          {swapping && (
            <Text
              style={{
                fontSize: 'var(--text-small)', color: 'var(--text-tertiary-soft)',
                marginRight: 'auto',
              }}
            >
              {t('v2.seats.swapNote', { date: day(scheduled?.effective_at ?? null) })}
            </Text>
          )}
          <Button variant="ghost" size="sm" onClick={discard} disabled={busy}>
            {t(swapping ? 'v2.seats.cancelSwap' : 'v2.seats.discard')}
          </Button>
          <Button
            variant="solid"
            size="sm"
            data-testid="plan-seats-confirm"
            onClick={confirm}
            disabled={!canConfirm}
          >
            {payLabel(swapping, growing, quoted, money(chargedToday), t)}
          </Button>
        </div>
      )}
    </div>
  );
};
